import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const SPREADSHEET_URL = "/accepted-scholars.xlsx";

/** Canonical column order for the accepted-scholars list. */
const SCHOLAR_LIST_SCHEMA = [
  "S/N",
  "NAME",
  "INSTITUTION",
  "COURSE OF STUDY",
  "LEVEL",
  "ACCOUNT NUMBER",
  "BANK",
] as const;

function cellToString(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return String(value).trim();
}

function normalizeHeaderKey(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Maps normalized spreadsheet header text → schema column index. */
const HEADER_ALIASES: Record<string, number> = (() => {
  const m: Record<string, number> = {};
  const add = (variants: string[], schemaIndex: number) => {
    for (const v of variants) {
      m[normalizeHeaderKey(v)] = schemaIndex;
    }
  };
  add(["S/N", "S/N.", "SN", "Serial No", "Serial Number", "S No"], 0);
  add(["Name"], 1);
  add(["Institution"], 2);
  add(["Course of Study", "Course"], 3);
  add(["Level"], 4);
  add(["Account", "Account No", "Account Number"], 5);
  add(["Bank"], 6);
  return m;
})();

function parseSheetRows(raw: unknown[][]): { headers: string[]; rows: string[][] } {
  if (!raw.length) {
    return { headers: [], rows: [] };
  }
  const width = Math.max(0, ...raw.map((r) => r.length));
  const headers = Array.from({ length: width }, (_, i) =>
    cellToString(raw[0][i] ?? "")
  );
  const rows = raw.slice(1).map((row) =>
    Array.from({ length: width }, (_, i) => cellToString(row[i]))
  );
  return { headers, rows };
}

function mapRowsToSchema(
  fileHeaders: string[],
  dataRows: string[][]
): { headers: string[]; rows: string[][] } {
  const schemaHeaders = [...SCHOLAR_LIST_SCHEMA];
  const colForSchema: number[] = schemaHeaders.map((_, schemaIdx) => {
    const j = fileHeaders.findIndex((fh) => HEADER_ALIASES[normalizeHeaderKey(fh)] === schemaIdx);
    return j;
  });
  const usePositional = colForSchema.every((j) => j < 0);
  const pickCol = (schemaIdx: number, row: string[]) => {
    const j = usePositional ? schemaIdx : colForSchema[schemaIdx];
    if (j < 0 || j >= row.length) return "";
    return row[j] ?? "";
  };
  const rows = dataRows
    .map((row) => schemaHeaders.map((_, i) => pickCol(i, row)))
    .filter((row) => row.some((c) => c !== ""));
  return { headers: schemaHeaders, rows };
}

export default function AcceptedScholars() {
  const [query, setQuery] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(SPREADSHEET_URL);
        if (!res.ok) {
          throw new Error(
            res.status === 404
              ? "List file is missing. Add public/accepted-scholars.xlsx and redeploy."
              : `Could not load list (${res.status})`
          );
        }
        const buf = await res.arrayBuffer();
        const XLSX = await import("xlsx");
        const wb = XLSX.read(buf, { type: "array" });
        const sheetName = wb.SheetNames[0];
        if (!sheetName) {
          throw new Error("Spreadsheet has no sheets");
        }
        const sheet = wb.Sheets[sheetName];
        const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
          header: 1,
          defval: "",
          raw: false,
        }) as unknown[][];
        const parsed = parseSheetRows(matrix);
        const mapped = mapRowsToSchema(parsed.headers, parsed.rows);
        if (!cancelled) {
          setHeaders(mapped.headers);
          setRows(mapped.rows);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load spreadsheet");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      row.some((cell) => cell.toLowerCase().includes(q))
    );
  }, [rows, query]);

  return (
    <div className="min-h-screen bg-sky-50 px-4 py-10 text-slate-900 sm:px-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600">
              Jantabo Scholarship Initiative
            </p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-900">
              Accepted scholarship applications
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Columns: {SCHOLAR_LIST_SCHEMA.join(" · ")}. First row in the file should
              name the columns; search matches any field.
            </p>
          </div>
          <Link
            to="/login"
            className="text-sm font-medium text-blue-700 underline-offset-4 hover:underline"
          >
            Portal sign in
          </Link>
        </div>

        <div className="glass-card rounded-3xl p-5 sm:p-6">
          <label className="block text-sm font-medium text-slate-700">
            Search
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Institution, course, account, bank…"
              disabled={loading || !!error || !headers.length}
              className="mt-2 w-full rounded-2xl border border-sky-200 bg-white px-4 py-3 text-slate-900 outline-none ring-blue-200 transition focus:ring-2 disabled:cursor-not-allowed disabled:opacity-60"
              autoComplete="off"
            />
          </label>
          <p className="mt-2 text-xs text-slate-500">
            {loading
              ? "Loading…"
              : error
                ? error
                : `${filteredRows.length} of ${rows.length} row${rows.length === 1 ? "" : "s"} shown`}
          </p>
        </div>

        {!loading && !error && headers.length > 0 && (
          <div className="glass-card overflow-hidden rounded-3xl">
            <div className="max-h-[min(70vh,720px)] overflow-auto">
              <table className="min-w-full border-collapse text-left text-sm">
                <thead className="sticky top-0 z-10 bg-sky-100/95 backdrop-blur">
                  <tr>
                    {headers.map((h, i) => (
                      <th
                        key={i}
                        className="whitespace-nowrap border-b border-sky-200 px-4 py-3 font-semibold text-slate-800"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, ri) => (
                    <tr
                      key={ri}
                      className={ri % 2 === 0 ? "bg-white" : "bg-sky-50/60"}
                    >
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          className="border-b border-sky-100 px-4 py-2.5 text-slate-700"
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !error && headers.length === 0 && (
          <p className="text-center text-sm text-slate-600">
            The spreadsheet is empty.
          </p>
        )}
      </div>
    </div>
  );
}
